import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiCall } from '../AuthContext';
import { Layers, Send, CheckCircle } from 'lucide-react';

export default function ContactUs() {
  const [form, setForm] = useState({ name: '', email: '', message: '', purpose: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiCall('/api/contact', { method: 'POST', data: form });
      setSent(true);
    } catch {
      alert('Failed to send. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="contact-page">
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
              <img src="/logo.png" alt="XLabelSync" className="w-8 h-8 object-cover" />
            </div>
            <span className="font-bold text-lg tracking-tight text-[#0F172A]">XLabelSync</span>
          </Link>
          <Link to="/login" className="text-sm font-medium text-[#0052FF]">Log in</Link>
        </div>
      </header>

      <div className="pt-32 pb-20 px-6 max-w-lg mx-auto">
        {sent ? (
          <div className="text-center" data-testid="contact-success">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Message Sent!</h2>
            <p className="text-slate-500">We'll get back to you shortly.</p>
            <Link to="/" className="inline-block mt-6 text-[#0052FF] font-medium">Back to Home</Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Contact Us</h1>
            <p className="text-slate-500 mb-8">Have a question or need help? We're here for you.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Name</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  placeholder="Your name" data-testid="contact-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Email</label>
                <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  placeholder="you@example.com" data-testid="contact-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Purpose</label>
                <select value={form.purpose} onChange={e => setForm(p => ({ ...p, purpose: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  data-testid="contact-purpose">
                  <option value="">Select...</option>
                  <option value="sales">Sales Inquiry</option>
                  <option value="support">Technical Support</option>
                  <option value="billing">Billing</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Message</label>
                <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} required rows={4}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm resize-none"
                  placeholder="How can we help?" data-testid="contact-message" />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="w-full bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg py-2.5 text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
